import styles from './Roles.module.css';

import React from 'react';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import { DateTime } from 'luxon';

import { RoleRemoval } from './RoleRemoval.jsx';

// MARK: Role
const Role = ({ role, isUnscheduled, isAssigned, onRoleSelect, isRemovalMode, setRemovalMode, removalReason, removalNote, canAssign }) => {
    const handleRoleSelect = () => {
        if (isRemovalMode && isAssigned) {
            if (isUnscheduled) {
                onRoleSelect({ id: 'unscheduled' }, { reason: removalReason, note: removalNote });
            } else {
                onRoleSelect(role, { reason: removalReason, note: removalNote });
            }

            setRemovalMode(false);
        } else if (!isAssigned) {
            onRoleSelect(role);
        }
    };

    // In removal mode: only show if assigned to this role
    // In normal mode: only show if can assign (within event limit)
    if (isRemovalMode && !isAssigned) return null;
    if (!isRemovalMode && !canAssign) return null;

    // Disable remove button if no reason selected
    const isRemoveDisabled = isRemovalMode && isAssigned && (!removalReason || (removalReason === 'other' && !removalNote?.trim()));

    return (
        <Button
            variant="contained"
            color={ isRemovalMode && isAssigned ? 'error' : 'primary' }
            onClick={ handleRoleSelect }
            disabled={ !isRemovalMode && role.available <= 0 || isRemoveDisabled || (!isRemovalMode && isAssigned) }
            disableElevation
        >
            { isRemovalMode && isAssigned
                ? 'Remove Assignment'
                : !isRemovalMode && isAssigned ?
                    `Scheduled (${ role.name })`
                    : `${ role.name } (${ role.available } slot${ role.available !== 1 ? 's' : '' } left)`
            }
        </Button>
    );
};

const CertificationAlert = ({ missingRoles }) => {
    if (!missingRoles?.length) return null;

    return (
        <Alert
            variant='filled'
            severity="warning"
            className="certification-alert"
            sx={ {
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                color: 'rgba(255, 255, 255, 0.87)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                '& .MuiAlert-icon': {
                    color: 'rgba(255, 193, 7, 0.87)',
                },
            } }
        >
            <strong>Missing Certifications:</strong>
            <ul style={ { margin: '8px 0 0 0', paddingLeft: '20px' } }>
                { missingRoles.map(role => (
                    <li key={ role.id }>
                        <strong>{ role.name }</strong> requires certification.
                    </li>
                )) }
            </ul>
        </Alert>
    );
};

// MARK: Roles
export const Roles = ({ shift, patroller, onRoleSelect }) => {
    const [ isRemovalMode, setRemovalMode ] = React.useState(false);
    const [ removalReason, setRemovalReason ] = React.useState('');
    const [ removalNote, setRemovalNote ] = React.useState('');

    // Separate available slots by certification eligibility
    const { availableSlotRolePairs, missingCertificationRoles } = React.useMemo(() => {
        const available = [];
        const missing = [];

        // For each slot, find the matching role (by id)
        const slotRolePairs = shift.slots?.map(slot => {
            const role = shift.roles.find(r => r.id === slot.id && slot.count > 0);

            return { slot, role };
        })?.filter(({ role }) => !!role);

        slotRolePairs?.forEach(({ slot, role }) => {
            const patrollerInRole = [ role.id, 'unscheduled' ].includes(patroller.shifts.find(s => s.id === shift.id)?.role);
            const remainingSlots = role.available;  //remainingSlotsForSlot(slot, role);
            const isAssignedToThisRole = patrollerInRole || shift.patrollers?.some(p =>
                p.id === patroller.id &&
                (role.certification
                    ? patroller.certifications?.some(c => c.id === role.certification)
                    : true)
            );

            const patrollerHasCert = () =>
                !role.certification || role.certification === 'none' || role.certification === '' ||
                patroller.certifications?.some(c => c.id === role.certification);

            // Show if there are open slots OR if patroller is assigned
            const shouldShow = remainingSlots > 0 || isAssignedToThisRole;

            if (shouldShow) {
                if (patrollerHasCert() || isAssignedToThisRole) {
                    available.push({ slot, role });
                } else if (remainingSlots > 0) {
                    missing.push({
                        id: role.id,
                        name: role.name,
                        remainingSlots
                    });
                }
            }
        });

        return { availableSlotRolePairs: available, missingCertificationRoles: missing };
    }, [ shift.slots, shift.roles, shift.patrollers, shift.id, patroller.shifts, patroller.id, patroller.certifications ]);

    // Reset form when removal mode is toggled off
    React.useEffect(() => {
        if (!isRemovalMode) {
            setRemovalReason('');
            setRemovalNote('');
        }
    }, [ isRemovalMode ]);

    // The server decides whether this patroller may sign up for the event. Unknown (null/missing)
    // shows the buttons; the server still enforces on the assignment call.
    const signUp = patroller.signUp?.[shift.event?.id];
    const canSelectRoles = signUp?.allowed !== false;

    const opensAtTime = signUp?.opensAt ? DateTime.fromMillis(Number(signUp.opensAt)) : null;
    const opens = opensAtTime?.isValid ? { date: opensAtTime.toFormat('MMM d'), time: opensAtTime.toLocaleString(DateTime.TIME_SIMPLE) } : null;

    // Count shifts with actual roles (not unscheduled) for this event
    const actualRoleShiftsCount = patroller.shifts?.filter(s => {
        return s.role && s.role !== 'unscheduled' && s.event.id === shift.event.id
    }).length || 0;

    // Event limit: if limit > 0, enforce it; otherwise, no limit
    const eventLimit = shift.event?.limit;
    const hasEventLimit = eventLimit > 0;

    // Check if patroller is already assigned to any slot in this shift
    const isAssigned = patroller.shifts?.some(s => s.id === shift.id && s.role !== null);
    const isUnscheduled = patroller.shifts.some(s => s.id === shift.id && s.role === 'unscheduled');

    const patrollerInRole = (role) => isUnscheduled || patroller.shifts.some(s => s.id === shift.id && s.role === role.id);
            
    const isAssignedToThisRole = (role) => {
        return patrollerInRole(role) || shift.patrollers?.some(p => {
            const patrollerId = typeof p === 'object' && p !== null ? p.id : p;
            
            return patrollerId === patroller.id &&
                patroller.shifts.some(s => s.id === shift.id && s.role === role.id) &&
                (role.certification
                    ? patroller.certifications?.some(c => c.id === role.certification)
                    : true);
        });
    };
                
    return (
        <div className={ styles.roles }>
            {/* Show removal section only if patroller is assigned */ }
            { isAssigned && (
                <RoleRemoval
                    isRemovalMode={ isRemovalMode }
                    setRemovalMode={ setRemovalMode }
                    removalReason={ removalReason }
                    setRemovalReason={ setRemovalReason }
                    removalNote={ removalNote }
                    setRemovalNote={ setRemovalNote }
                />
            ) }

            { !isAssigned && <CertificationAlert missingRoles={ missingCertificationRoles } /> }

            { !canSelectRoles && (
                <Alert
                    variant='filled'
                    severity="info"
                    sx={ {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        color: 'rgba(255, 255, 255, 0.87)',
                        border: '1px solid rgba(33, 150, 243, 0.3)',
                    } }
                >
                    <AlertTitle>Registration Not Open</AlertTitle>

                    <Typography>
                        { !opens
                            ? 'Registration is not open yet.'
                            : DateTime.now().toFormat('MMM d') === opens.date
                                ? `Event registration opens at ${ opens.time } today.`
                                : `Event registration opens on ${ opens.date } at ${ opens.time }.` }
                    </Typography>
                </Alert>
            ) }

            { canSelectRoles && availableSlotRolePairs?.length > 0 ? (
                availableSlotRolePairs
                    .filter(({ role }) => {
                        // In removal mode: only show assigned role
                        if (isRemovalMode) {
                            return isAssignedToThisRole(role);
                        }

                        // In normal mode: if assigned to ANY role, only show that assigned role
                        if (isAssigned) {
                            return isAssignedToThisRole(role);
                        }

                        // Not assigned: show all available roles
                        return true;
                    })
                    .map(({ slot, role }) => {
                        return (
                            <Role
                                key={ slot.id }
                                role={ role }
                                isUnscheduled={ isUnscheduled }
                                isAssigned={ isAssignedToThisRole(role) }
                                onRoleSelect={ onRoleSelect }
                                isRemovalMode={ isRemovalMode }
                                setRemovalMode={ setRemovalMode }
                                removalReason={ removalReason }
                                removalNote={ removalNote }
                                canAssign={ !hasEventLimit || actualRoleShiftsCount < eventLimit }
                            />
                        );
                    })
            ) :
                !missingCertificationRoles && !isRemovalMode && (<div>No roles available</div>)
            }

            { !isRemovalMode && hasEventLimit && actualRoleShiftsCount >= eventLimit && (
                <Alert
                    variant='filled'
                    severity="info"
                    sx={ {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        color: 'rgba(255, 255, 255, 0.87)',
                        border: '1px solid rgba(33, 150, 243, 0.3)',
                    } }
                >
                    <AlertTitle>Maximum Shifts Reached</AlertTitle>

                    <Typography>
                        { shift.event?.allowUnscheduled && (shift.event?.allowUnscheduledAfter ? shift.available === 0 : true)
                            ? 'You have reached the maximum number of scheduled shifts. You may select Unscheduled for this shift.'
                            : 'You have reached the maximum number of scheduled shifts.' }
                    </Typography>
                </Alert>
            ) }

            {/* Show unscheduled option only when not in removal mode and not assigned */ }
            { !isAssigned && !isRemovalMode && shift.event?.allowUnscheduled && (shift.event?.allowUnscheduledAfter ? shift.available === 0 : true) && (
                <Box justifyContent="center" alignItems="center" display="flex" flexDirection="column" sx={ { width: '100%' } }>
                    <Divider flexItem sx={ { mt: 1, mb: 2 } } />

                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={ () => onRoleSelect({ id: 'unscheduled' }) }
                        disableElevation
                        className={ styles.unscheduledButton }
                    >
                        Unscheduled
                    </Button>
                </Box>
            ) }
        </div>
    );
};